import { Section } from '@/components/builder/inspector-controls';
import { Field, NumberInput, SelectInput, TextInput } from '@/components/builder/inspector-fields';
import { findStep, type SessionStore } from '@personalizehq/editor';
import { surfaceSchema, type SurfaceKind } from '@personalizehq/schemas';
import { useStore } from 'zustand';

// B3 — THE FIELDS NOBODY COULD AUTHOR.
//
// `step.surface` has existed in the schema since P3 and NO CONTROL HAS EVER SET IT: a step was
// whatever the template happened to make it, and `inline` — a v1 requirement, pinned item 12 — was
// literally unreachable from the dashboard. Same for `step.name` (the strip renders it and offers no
// rename) and `advance` (schema-only).
//
// Home is the inspector's empty-selection state, which until now said "Select a block" and nothing
// else. That space was already the right one: nothing is selected, so the thing you are looking at
// IS the step.

const SURFACES = surfaceSchema.options;

/** Every step, so the surface picker can tell the truth about the ONE-STEP law below. */
export function StepSection({ session }: { session: SessionStore }): React.JSX.Element {
  const doc = useStore(session, (s) => s.doc);
  const focusedStepId = useStore(session, (s) => s.focusedStepId);
  const step = findStep(doc, focusedStepId);

  if (!step) return <p className="p-3 text-[12px] text-muted-foreground">This document has no steps.</p>;

  const edit = (field: string, mutate: (s: NonNullable<typeof step>) => void): void =>
    session.getState().applyChange((draft) => {
      const target = findStep(draft, focusedStepId);
      if (target) mutate(target);
    }, `${focusedStepId}.${field}`);

  const timeout = step.advance?.find((rule) => rule.when === 'timeout');
  const multi = doc.steps.length > 1;

  return (
    <>
      <Section title="Step">
        <Field label="name" set>
          <TextInput
            aria-label="step name"
            value={step.name}
            onChange={(name) => edit('name', (s) => void (s.name = name || 'Step'))}
          />
        </Field>

        {/* THE ONE THAT UNBLOCKS v1. An `inline` step is a widget in a slot in the host's own
            screen — so the campaign that carries it must be an `inline` placement, and it must be a
            SINGLE step ("a widget is not a flow" — bundle.ts). Both laws are the campaign's to
            enforce, because only it knows the placement; what this control owes the author is the
            truth about them, said BEFORE they pick, not as a publish rejection afterwards. */}
        <Field label="surface" set>
          <SelectInput
            aria-label="surface"
            value={step.surface}
            options={SURFACES}
            onChange={(surface: SurfaceKind) => edit('surface', (s) => void (s.surface = surface))}
          />
        </Field>
        {step.surface === 'inline' ? (
          <p className="text-[11px] text-muted-foreground">
            A widget in the host&rsquo;s screen. Its campaign needs an <b>inline placement</b> and a
            slot.
            {multi ? (
              <span className="text-amber-500">
                {' '}
                This document has {doc.steps.length} steps — an inline experience is a single step,
                and publish will refuse it.
              </span>
            ) : null}
          </p>
        ) : null}

        {/* `advance` is a list in the schema, but `timeout` is the only rule it has ever had, so the
            control is one number rather than a rule builder for a union of one. */}
        <Field
          label="auto advance"
          set={timeout !== undefined}
          onUnset={() => edit('advance', (s) => void delete s.advance)}
        >
          <NumberInput
            aria-label="step auto advance"
            min={1}
            step={500}
            value={timeout?.ms}
            onChange={(ms) =>
              edit('advance', (s) => {
                if (ms === undefined) delete s.advance;
                else s.advance = [{ when: 'timeout', ms }];
              })
            }
          />
          <span className="text-[11px] text-muted-foreground">ms</span>
        </Field>
      </Section>

      <p className="p-3 text-[12px] text-muted-foreground">
        Select a block on the canvas or in the layers tree to edit it.
      </p>
    </>
  );
}
